import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminCompanies, adminConfig } from '../../api/adminClient'
import ConfirmModal from '../../components/admin/ConfirmModal'
import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'

export default function AdminCompanyDetail() {
  const { t } = useTranslation('portal')
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [suspendModal, setSuspendModal] = useState(false)
  const [activateModal, setActivateModal] = useState(false)
  const [feeForm, setFeeForm] = useState<{
    bank_fee_percent: string
    bank_fee_fixed: string
    platform_fee_percent: string
    platform_fee_fixed: string
  }>({ bank_fee_percent: '', bank_fee_fixed: '', platform_fee_percent: '', platform_fee_fixed: '' })

  const { data: company, isLoading, error } = useQuery({
    queryKey: ['admin', 'company', id],
    queryFn: () => adminCompanies.get(id!).then((r) => r.data),
    enabled: !!id,
  })

  const suspend = useMutation({
    mutationFn: () => adminCompanies.suspend(id!),
    onSuccess: () => {
      setSuspendModal(false)
      queryClient.invalidateQueries({ queryKey: ['admin', 'company', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] })
    },
  })

  const activate = useMutation({
    mutationFn: () => adminCompanies.activate(id!),
    onSuccess: () => {
      setActivateModal(false)
      queryClient.invalidateQueries({ queryKey: ['admin', 'company', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] })
    },
  })

  const { data: companyFees } = useQuery({
    queryKey: ['admin', 'config', 'fees', 'company', id],
    queryFn: () => adminConfig.getCompanyFees(id!).then((r) => r.data),
    enabled: !!id,
  })

  const updateCompanyFeesMutation = useMutation({
    mutationFn: (data: {
      bank_fee_percent: number
      bank_fee_fixed: number
      platform_fee_percent: number
      platform_fee_fixed: number
    }) => adminConfig.updateCompanyFees(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'fees', 'company', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'fees'] })
    },
  })

  if (!id) return <div className="text-red-600">{t('admin.companyDetail.missingCompanyId')}</div>
  if (isLoading) return <div className="text-gray-500">{t('admin.common.loading')}</div>
  if (error || !company) return <div className="text-red-600">{t('admin.companyDetail.companyNotFound')}</div>

  const c = company as {
    id: string
    name: string
    tin: string
    business_unit?: string
    address?: string
    user_id: string
    is_active: string
    is_verified: string
    created_at: string
    updated_at: string
  }

  return (
    <div>
      <Link
        to="/admin/companies"
        className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> {t('admin.companyDetail.backToCompanies')}
      </Link>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{c.name}</h1>
      <div className="rounded-xl bg-white border border-gray-200 p-6 space-y-4 max-w-2xl shadow-sm">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <span className="text-gray-500">{t('admin.companies.tin')}</span>
          <span className="text-gray-900">{c.tin}</span>
          <span className="text-gray-500">{t('admin.common.status')}</span>
          <span>
            <span className={c.is_verified === 'true' ? 'text-emerald-600' : 'text-amber-600'}>
              {c.is_verified === 'true' ? t('admin.companies.verified') : t('admin.companies.pending')}
            </span>
            {c.is_active === 'false' && <span className="ml-2 text-red-600">{t('admin.dashboard.suspended')}</span>}
          </span>
          <span className="text-gray-500">{t('admin.companyDetail.userId')}</span>
          <span className="text-gray-600 font-mono text-xs">{c.user_id}</span>
          {c.address && (
            <>
              <span className="text-gray-500">{t('admin.companyDetail.address')}</span>
              <span className="text-gray-700">{c.address}</span>
            </>
          )}
          <span className="text-gray-500">{t('admin.common.created')}</span>
          <span className="text-gray-600">{new Date(c.created_at).toLocaleString()}</span>
        </div>
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          {c.is_active === 'true' ? (
            <button
              type="button"
              onClick={() => setSuspendModal(true)}
              className="px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm"
            >
              {t('admin.companyDetail.suspendCompany')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setActivateModal(true)}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 text-sm"
            >
              {t('admin.companyDetail.activateCompany')}
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white border border-gray-200 p-6 mt-6 max-w-2xl shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-2">{t('admin.companyDetail.feeOverrideTitle')}</h2>
        <p className="text-gray-500 text-sm mb-4">{t('admin.companyDetail.feeOverrideDesc')}</p>
        {companyFees && (
          <form
            className="space-y-4 max-w-md"
            onSubmit={(e) => {
              e.preventDefault()
              // Use form values if set, otherwise fall back to displayed companyFees (so Save works without editing)
              const bPct = parseFloat(feeForm.bank_fee_percent || String(companyFees?.bank_fee_percent ?? companyFees?.bank_fee_value ?? 0))
              const bFix = parseFloat(feeForm.bank_fee_fixed || String(companyFees?.bank_fee_fixed ?? 0))
              const pPct = parseFloat(feeForm.platform_fee_percent || String(companyFees?.platform_fee_percent ?? 0))
              const pFix = parseFloat(feeForm.platform_fee_fixed || String(companyFees?.platform_fee_fixed ?? 0))
              if (!Number.isFinite(bPct) || !Number.isFinite(bFix) || !Number.isFinite(pPct) || !Number.isFinite(pFix)) return
              updateCompanyFeesMutation.mutate({
                bank_fee_percent: bPct,
                bank_fee_fixed: bFix,
                platform_fee_percent: pPct,
                platform_fee_fixed: pFix,
              })
            }}
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.bankFeePercent')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={String((feeForm.bank_fee_percent || (companyFees?.bank_fee_percent ?? companyFees?.bank_fee_value ?? '')) ?? '')}
                onChange={(e) => setFeeForm((s) => ({ ...s, bank_fee_percent: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">%</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.bankFeeFixed')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={String((feeForm.bank_fee_fixed || (companyFees?.bank_fee_fixed ?? '')) ?? '')}
                onChange={(e) => setFeeForm((s) => ({ ...s, bank_fee_fixed: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">BAM</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.platformFeePercent')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={String((feeForm.platform_fee_percent || (companyFees?.platform_fee_percent ?? '')) ?? '')}
                onChange={(e) => setFeeForm((s) => ({ ...s, platform_fee_percent: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">%</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.settings.platformFeeFixed')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={String((feeForm.platform_fee_fixed || (companyFees?.platform_fee_fixed ?? '')) ?? '')}
                onChange={(e) => setFeeForm((s) => ({ ...s, platform_fee_fixed: e.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 w-32 focus:ring-2 focus:ring-emerald-500"
              />
              <span className="ml-2 text-gray-500 text-sm">BAM</span>
            </div>
            <button
              type="submit"
              disabled={updateCompanyFeesMutation.isPending}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm hover:bg-emerald-500 disabled:opacity-50"
            >
              {updateCompanyFeesMutation.isPending ? '…' : t('admin.companyDetail.saveCompanyFees')}
            </button>
            {updateCompanyFeesMutation.isError && (
              <p className="text-sm text-red-600">
                {(updateCompanyFeesMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('admin.settings.saveDefaultFeesError')}
              </p>
            )}
          </form>
        )}
      </div>

      <ConfirmModal
        open={suspendModal}
        title={t('admin.companyDetail.suspendConfirmTitle')}
        variant="danger"
        message={t('admin.companyDetail.suspendConfirmMessage')}
        confirmLabel={t('admin.companyDetail.suspend')}
        cancelLabel={t('admin.common.cancel')}
        onConfirm={() => suspend.mutate()}
        onCancel={() => setSuspendModal(false)}
        loading={suspend.isPending}
      />
      <ConfirmModal
        open={activateModal}
        title={t('admin.companyDetail.activateConfirmTitle')}
        message={t('admin.companyDetail.activateConfirmMessage')}
        confirmLabel={t('admin.companyDetail.activate')}
        cancelLabel={t('admin.common.cancel')}
        onConfirm={() => activate.mutate()}
        onCancel={() => setActivateModal(false)}
        loading={activate.isPending}
      />
    </div>
  )
}
